// KPI / stat card for the v2 dashboard — matches Figma 'Statistika' (node 19005:2854).
// Label (uppercase) + optional info tooltip, a large value, an optional change badge
// (green when the change is "good", red otherwise), and a comparison caption.
// `aiStyled` renders the AI variant (robot icon + gradient border) used for estimated savings.
import React, { ReactNode } from 'react';
import SvgArrowUp from '@icons/arrow-up.svg?react';
import SvgArrowDown from '@icons/arrow-down.svg?react';
import SvgRobot from '@icons/ai-robot.svg?react';
import DashboardInfoTooltip from './DashboardInfoTooltip.tsx';

interface DashboardKpiCardProps {
  label: string;
  value: ReactNode;
  // KpiDTO.changePct — percentage change vs. the comparison period (e.g. 8.3 → "8.3 %").
  changePct?: number;
  // Caption under the value, e.g. "vs. prior 30 days".
  comparisonLabel?: string;
  // When provided, shows an info icon with this tooltip content.
  tooltip?: ReactNode;
  // Whether a positive change should be rendered as good (green). Set false for cost/time KPIs
  // where an increase is bad. Arrow direction always follows the sign of the change.
  positiveIsGood?: boolean;
  // AI variant: robot icon before the label + gradient border (e.g. estimated savings).
  aiStyled?: boolean;
}

const SHADOW = 'shadow-[0px_2px_8px_0px_rgba(0,0,0,0.05),0px_16px_48px_0px_rgba(254,80,0,0.02)]';

// AI (estimated savings) card styling — Figma "Gradient/AI Back": a rainbow gradient *border*
// with a faint AI-tinted white interior. The border gradient is painted via border-box while the
// interior (white + 5% gradient tint) is painted via padding-box (standard gradient-border trick).
// Light blue → orange → red, matching the design's direction (no green).
const AI_GRADIENT = 'linear-gradient(130deg, #00B5E2 0%, #FE5000 55%, #E4002B 100%)';
const AI_CARD_STYLE: React.CSSProperties = {
  borderRadius: '4px',
  border: '1px solid transparent',
  background: [
    'linear-gradient(130deg, rgba(0,181,226,0.05) 0%, rgba(254,80,0,0.05) 55%, rgba(228,0,43,0.05) 100%) padding-box',
    'linear-gradient(#fff, #fff) padding-box',
    `${AI_GRADIENT} border-box`,
  ].join(', '),
  boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.05), 0 16px 48px 0 rgba(254, 80, 0, 0.02)',
};

const DashboardKpiCard: React.FC<DashboardKpiCardProps> = ({ label, value, changePct, comparisonLabel, tooltip, positiveIsGood = true, aiStyled = false }) => {
  const hasChange = changePct != null && !Number.isNaN(changePct);
  const isUp = (changePct ?? 0) >= 0;
  const isGood = isUp === positiveIsGood;

  const content = (
    <>
      <div className="flex items-center gap-2 w-full">
        {aiStyled && <SvgRobot className="w-[17px] h-[17px] shrink-0" />}
        <span className="text-[11px] uppercase text-content-secondary leading-normal">{label}</span>
        <DashboardInfoTooltip content={tooltip} />
      </div>
      <div className="flex flex-col gap-1 items-start w-full">
        <div className="flex flex-wrap content-center items-center gap-2 w-full">
          <span className="text-2xl font-medium text-content-primary tracking-[-0.48px] leading-normal">{value}</span>
          {hasChange && (
            <span className={`inline-flex items-center justify-center gap-1 px-1 py-0.5 rounded-lg ${isGood ? 'bg-complementary-successMedium' : 'bg-complementary-errorMedium'}`}>
              {isUp ? <SvgArrowUp className="w-2 h-2" /> : <SvgArrowDown className="w-2 h-2" />}
              <span className="text-[10px] text-content-secondary leading-3">{Math.abs(changePct).toFixed(1)} %</span>
            </span>
          )}
        </div>
        {hasChange && comparisonLabel && <span className="text-xs text-content-tertiary leading-normal">{comparisonLabel}</span>}
      </div>
    </>
  );

  if (aiStyled) {
    return (
      <div className="flex flex-col items-end gap-2 p-4 h-full" style={AI_CARD_STYLE}>
        {content}
      </div>
    );
  }

  return <div className={`flex flex-col items-end gap-2 p-4 h-full bg-white border border-solid border-content-gr2 rounded ${SHADOW}`}>{content}</div>;
};

export default DashboardKpiCard;